import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Sun, ChevronDown, LogOut, User as UserIcon } from "lucide-react";
import { LANGUAGES, useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const primaryLinks = (t) => [
  { to: "/", label: t("nav.home") },
  { to: "/book", label: t("nav.book") },
  { to: "/fundraiser", label: t("nav.fundraiser") },
  { to: "/contact", label: t("nav.contact") },
];

const exploreLinks = (t) => [
  { to: "/what-is-reiki", label: t("nav.reiki") },
  { to: "/mission", label: t("nav.mission") },
  { to: "/future-services", label: t("nav.future_services") },
  { to: "/future-interest", label: t("nav.future_interest") },
  { to: "/support", label: t("nav.support") },
  { to: "/certificates", label: t("nav.certificates") },
];

function LanguageSwitcher() {
  const { lang, setLang } = useLang();
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="language-switcher-trigger"
        className="inline-flex items-center gap-1.5 rounded-full border border-forest-900/15 bg-white/60 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-forest-800 transition hover:border-wheat-500/60 hover:bg-white"
      >
        <span>{current.short}</span>
        <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem] rounded-xl bg-ivory border border-forest-900/10">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-forest-700/70">Language</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LANGUAGES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`lang-option-${l.code}`}
            onSelect={() => setLang(l.code)}
            className={`cursor-pointer text-sm ${lang === l.code ? "text-forest-900 font-semibold" : "text-forest-700"}`}
          >
            {l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AccountMenu() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  if (!user) {
    return (
      <div className="hidden lg:flex items-center gap-3">
        <Link data-testid="header-login-link" to="/login" className="text-xs font-semibold uppercase tracking-widest text-forest-800 hover:text-forest-900">
          {t("nav.login")}
        </Link>
        <Link data-testid="header-book-cta" to="/book" className="btn-primary !py-2 !text-[11px]">
          {t("nav.book_cta")}
        </Link>
      </div>
    );
  }
  return (
    <div className="hidden lg:flex items-center gap-3">
      <DropdownMenu>
        <DropdownMenuTrigger data-testid="account-menu-trigger" className="inline-flex items-center gap-2 rounded-full border border-forest-900/15 bg-white/60 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-forest-800 hover:border-wheat-500/60">
          <UserIcon className="h-4 w-4" strokeWidth={1.5} />
          <span className="max-w-[8rem] truncate">{user.name}</span>
          <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-xl bg-ivory border border-forest-900/10">
          <DropdownMenuItem onSelect={() => navigate("/account")} data-testid="account-menu-dashboard">{t("nav.dashboard")}</DropdownMenuItem>
          {user.role === "admin" && (
            <DropdownMenuItem onSelect={() => navigate("/admin")} data-testid="account-menu-admin">{t("nav.admin")}</DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={async () => { await logout(); navigate("/"); }} data-testid="account-menu-logout" className="text-forest-800">
            <LogOut className="mr-2 h-4 w-4" strokeWidth={1.5} /> {t("nav.logout")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Link data-testid="header-book-cta" to="/book" className="btn-primary !py-2 !text-[11px]">
        {t("nav.book_cta")}
      </Link>
    </div>
  );
}

export default function Layout({ children }) {
  const { t } = useLang();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const links = primaryLinks(t);
  const explore = exploreLinks(t);
  const exploreActive = explore.some((l) => pathname.startsWith(l.to));

  return (
    <div className="min-h-screen flex flex-col bg-ivory text-forest-900 grain">
      <header data-testid="site-header" className="sticky top-0 z-40 border-b border-forest-900/5 bg-ivory/85 backdrop-blur-md">
        <div className="container-refined flex h-20 items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
            <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-forest-800 text-ivory">
              <Sun className="h-4 w-4" strokeWidth={1.5} />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-serif text-2xl tracking-tight text-forest-900 group-hover:text-forest-800">Sunbraider</span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-wheat-700">{t("brand.project")}</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `text-[13px] tracking-wide font-medium transition-colors whitespace-nowrap ${
                    isActive || (l.to !== "/" && pathname.startsWith(l.to))
                      ? "text-forest-900"
                      : "text-forest-700 hover:text-forest-900"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink
              to="/promo"
              data-testid="nav-link-promo"
              className={({ isActive }) =>
                `text-[13px] tracking-wide font-medium transition-colors whitespace-nowrap ${
                  isActive || pathname.startsWith("/promo")
                    ? "text-forest-900"
                    : "text-forest-700 hover:text-forest-900"
                }`
              }
            >
              {t("nav.promo")}
            </NavLink>
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="nav-explore-trigger"
                className={`inline-flex items-center gap-1 text-[13px] tracking-wide font-medium transition-colors whitespace-nowrap ${exploreActive ? "text-forest-900" : "text-forest-700 hover:text-forest-900"}`}
              >
                {t("nav.explore")} <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[14rem] rounded-xl bg-ivory border border-forest-900/10 z-50">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-forest-700/70">{t("nav.explore")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {explore.map((l) => (
                  <DropdownMenuItem
                    key={l.to}
                    data-testid={`nav-explore-${l.to.replace(/\//g, "") || "home"}`}
                    onSelect={() => navigate(l.to)}
                    className="cursor-pointer text-sm text-forest-800"
                  >
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <AccountMenu />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-full border border-forest-900/15 text-forest-800"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="h-4 w-4" strokeWidth={1.5} /> : <Menu className="h-4 w-4" strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div data-testid="mobile-menu" className="lg:hidden border-t border-forest-900/10 bg-ivory">
            <div className="container-refined py-6 flex flex-col gap-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${l.to.replace(/\//g, "") || "home"}`}
                  className="text-sm font-medium tracking-wide text-forest-800 hover:text-forest-900"
                >
                  {l.label}
                </NavLink>
              ))}
              <NavLink
                to="/promo"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-explore-promo"
                className="text-sm font-medium tracking-wide text-forest-800 hover:text-forest-900"
              >
                {t("nav.promo")}
              </NavLink>
              <div className="pt-2 border-t border-forest-900/10">
                <p className="eyebrow mb-2">{t("nav.explore")}</p>
                <div className="flex flex-col gap-2">
                  {explore.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`mobile-explore-${l.to.replace(/\//g, "") || "home"}`}
                      className="text-sm text-forest-700 hover:text-forest-900"
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              </div>
              <div className="pt-3 border-t border-forest-900/10 flex flex-col gap-3">
                {!user ? (
                  <>
                    <Link to="/login" onClick={() => setMobileOpen(false)} className="btn-secondary justify-center" data-testid="mobile-login-link">{t("nav.login")}</Link>
                    <Link to="/book" onClick={() => setMobileOpen(false)} className="btn-primary justify-center" data-testid="mobile-book-cta">{t("nav.book_cta")}</Link>
                  </>
                ) : (
                  <>
                    <Link to="/account" onClick={() => setMobileOpen(false)} className="btn-secondary justify-center" data-testid="mobile-account-link">{t("nav.dashboard")}</Link>
                    {user.role === "admin" && (
                      <Link to="/admin" onClick={() => setMobileOpen(false)} className="btn-secondary justify-center" data-testid="mobile-admin-link">{t("nav.admin")}</Link>
                    )}
                    <button
                      data-testid="mobile-logout-btn"
                      onClick={async () => { setMobileOpen(false); await logout(); navigate("/"); }}
                      className="btn-primary justify-center"
                    >{t("nav.logout")}</button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 relative z-[2]">{children}</main>

      <footer data-testid="site-footer" className="mt-24 border-t border-forest-900/10 bg-surface/60">
        <div className="container-refined py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-forest-800 text-ivory"><Sun className="h-4 w-4" strokeWidth={1.5}/></span>
              <span className="font-serif text-2xl text-forest-900">Sunbraider</span>
            </div>
            <p className="mt-4 text-sm text-forest-700/90 leading-relaxed max-w-xs">{t("brand.project")} — {t("common.footer_rights")}</p>
          </div>
          <div>
            <p className="eyebrow mb-3">{t("nav.contact")}</p>
            <a href="https://signal.me/#p/+31633136379" target="_blank" rel="noreferrer" className="text-sm text-forest-700 hover:text-forest-900 flex items-center gap-2" data-testid="footer-signal">
              Signal · 0633136379
            </a>
            <a href="https://wa.me/31633136379?text=Hoi%20Isa" target="_blank" rel="noreferrer" className="text-sm text-forest-700 hover:text-forest-900 block mt-1" data-testid="footer-whatsapp">
              WhatsApp · 0633136379
            </a>
            <a href="tel:0633136379" className="text-sm text-forest-700 block mt-1">Tel · 0633136379</a>
            <a href="mailto:info@sunbraider.nl" className="text-sm text-forest-700 block mt-1">info@sunbraider.nl</a>
            <p className="mt-4 text-xs text-forest-700/80 max-w-xs">Walstraat 58, 7411 GN Deventer — <em>post only</em>.</p>
          </div>
          <div>
            <p className="eyebrow mb-3">{t("nav.explore")}</p>
            <div className="flex flex-col gap-2 text-sm text-forest-700">
              <Link to="/what-is-reiki" className="hover:text-forest-900">{t("nav.reiki")}</Link>
              <Link to="/mission" className="hover:text-forest-900">{t("nav.mission")}</Link>
              <Link to="/future-services" className="hover:text-forest-900">{t("nav.future_services")}</Link>
              <Link to="/support" className="hover:text-forest-900">{t("nav.support")}</Link>
              <Link to="/certificates" className="hover:text-forest-900">{t("nav.certificates")}</Link>
              <Link to="/promo" className="hover:text-forest-900">{t("nav.promo")}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
